// src/pages/AdminPartners.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import { supabase } from "../supabaseClient";

const BG_URL =
  "https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/backgrounds/Background%20App%20Vert.png";
const TOPBAR_BG =
  "https://paoletexuwmeuatvbkvf.supabase.co/storage/v1/object/public/backgrounds/Top%20Bar%20Background.png";

export default function AdminPartners() {
  // layout/state
  const [menuOpen, setMenuOpen] = useState(false);
  const [cacheBust, setCacheBust] = useState("");
  const [imgLoaded, setImgLoaded] = useState(false);

  // auth / form state
  const [user, setUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // form fields
  const [name, setName] = useState("");
  const [type, setType] = useState("discount"); // discount | points
  const [shortDesc, setShortDesc] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [discount, setDiscount] = useState("");
  const [pointsRate, setPointsRate] = useState(1);
  const [logoUrl, setLogoUrl] = useState(""); // <-- now accept URL instead of file

  // preload bg / cache bust
  useEffect(() => setCacheBust(`cb=${Date.now()}`), []);
  useEffect(() => {
    if (!cacheBust) return;
    const pre = new Image();
    pre.onload = () => setImgLoaded(true);
    pre.src = `${BG_URL}?${cacheBust}`;
    return () => {
      pre.onload = null;
    };
  }, [cacheBust]);

  // load session user (optional guard — you can refine to admin-only)
  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setUser(data.session?.user ?? null);
    })();
    // listen to changes
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  // submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // basic validation
    if (!name.trim()) {
      setError("Please provide a partner name.");
      return;
    }
    if (!["discount", "points"].includes(type)) {
      setError("Partner type must be 'discount' or 'points'.");
      return;
    }

    setSubmitting(true);
    try {
      // Build insert object
      const insertObj = {
        name: name.trim(),
        type,
        short: shortDesc || null,
        description: description || null,
        address: address || null,
        phone: phone || null,
        website: website || null,
        discount: type === "discount" ? (discount || null) : null,
        points_rate: type === "points" ? (Number(pointsRate) || 1) : null,
        logo_url: logoUrl?.trim() || null, // use provided URL
      };

      const { error: insertError } = await supabase.from("partners").insert([insertObj]);

      if (insertError) throw insertError;

      setSuccessMessage("Partner created successfully!");
      // reset form (keep user logged)
      setName("");
      setType("discount");
      setShortDesc("");
      setDescription("");
      setAddress("");
      setPhone("");
      setWebsite("");
      setDiscount("");
      setPointsRate(1);
      setLogoUrl("");
    } catch (err) {
      console.error("Error creating partner:", err);
      setError(err.message || err.error || "Failed to create partner.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        fontFamily: '"Roboto", "Helvetica Neue", Arial, sans-serif',
        color: "white",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#163b24",
        backgroundImage: cacheBust ? `url("${BG_URL}?${cacheBust}")` : `url("${BG_URL}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Background fallback img */}
      <img
        src={cacheBust ? `${BG_URL}?${cacheBust}` : BG_URL}
        alt="background"
        style={{
          position: "fixed",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -2,
          pointerEvents: "none",
          opacity: imgLoaded ? 1 : 0,
        }}
        onLoad={() => setImgLoaded(true)}
      />
      <div style={{ position: "fixed", inset: 0, zIndex: -1, background: "rgba(7,34,17,0.22)", pointerEvents: "none" }} />

      {/* Topbar */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.75rem 1rem",
          backgroundImage: `url(${TOPBAR_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          color: "white",
          fontFamily: '"Bryndan Write", cursive',
          zIndex: 5,
          position: "relative",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => setMenuOpen((s) => !s)}
            style={{ background: "none", border: "none", color: "white", cursor: "pointer" }}
            aria-label="Toggle menu"
          >
            <Menu size={22} />
          </button>
          <h2 style={{ margin: 0, fontSize: 18 }}>Create Partner</h2>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Link to="/partners" style={{ color: "white", textDecoration: "none" }}>
            ← Partner list
          </Link>
        </div>
      </header>

      {/* sidebar */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: menuOpen ? 0 : -220,
          width: 220,
          height: "100%",
          backgroundColor: "rgba(30,90,40,0.98)",
          paddingTop: "4rem",
          transition: "left 0.28s ease",
          zIndex: 6,
        }}
      >
        <nav style={{ display: "flex", flexDirection: "column", gap: "1.25rem", paddingLeft: "1rem" }}>
          <Link to="/" onClick={() => setMenuOpen(false)} style={{ color: "white", textDecoration: "none", fontWeight: 700 }}>
            Home
          </Link>
          <Link to="/profile" onClick={() => setMenuOpen(false)} style={{ color: "white", textDecoration: "none", fontWeight: 700 }}>
            Profile
          </Link>
          <Link to="/partners" onClick={() => setMenuOpen(false)} style={{ color: "white", textDecoration: "none", fontWeight: 700 }}>
            Partners
          </Link>
        </nav>
      </div>

      <main style={{ padding: "1.5rem 1rem", position: "relative", zIndex: 4 }}>
        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            background: "rgba(255,255,255,0.06)",
            padding: 18,
            borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            color: "white",
          }}
        >
          <h3 style={{ marginTop: 0 }}>Add a new partner</h3>

          {/* show basic auth notice */}
          {!user && (
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: "rgba(255,255,255,0.03)" }}>
              <strong>Notice:</strong> You are not signed in. The partner will still be created but consider restricting this page to admins only.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gap: 10 }}>
              <label>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Name</div>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Partner name" required
                  style={{ width: "100%", padding: 10, borderRadius: 8, border: "none", outline: "none" }} />
              </label>

              <div style={{ display: "flex", gap: 8 }}>
                <label style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Type</div>
                  <select value={type} onChange={(e) => setType(e.target.value)}
                    style={{ width: "100%", padding: 10, borderRadius: 8, border: "none", outline: "none" }}>
                    <option value="discount">Discount</option>
                    <option value="points">Points</option>
                  </select>
                </label>

                <label style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Short description</div>
                  <input value={shortDesc} onChange={(e) => setShortDesc(e.target.value)} placeholder="Short summary"
                    style={{ width: "100%", padding: 10, borderRadius: 8, border: "none", outline: "none" }} />
                </label>
              </div>

              <label>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Full description</div>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4}
                  style={{ width: "100%", padding: 10, borderRadius: 8, border: "none", outline: "none" }} />
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address"
                  style={{ padding: 10, borderRadius: 8, border: "none", outline: "none" }} />
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone"
                  style={{ padding: 10, borderRadius: 8, border: "none", outline: "none" }} />
              </div>

              <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="Website (https://...)"
                style={{ padding: 10, borderRadius: 8, border: "none", outline: "none" }} />

              {type === "discount" ? (
                <label>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Discount text</div>
                  <input value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="e.g. 10% off"
                    style={{ width: "100%", padding: 10, borderRadius: 8, border: "none", outline: "none" }} />
                </label>
              ) : (
                <label>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>Points rate (points per €)</div>
                  <input type="number" min={0} value={pointsRate} onChange={(e) => setPointsRate(e.target.value)}
                    style={{ width: 160, padding: 10, borderRadius: 8, border: "none", outline: "none" }} />
                </label>
              )}

              {/* Logo URL input (replaced file upload) */}
              <label>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Logo URL (public Supabase link or any image URL)</div>
                <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://.../logo.png"
                  style={{ width: "100%", padding: 10, borderRadius: 8, border: "none", outline: "none" }} />
                <div style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                  Paste a public image URL (e.g. a Supabase public file URL from your `partners-logos` bucket).
                </div>
              </label>

              {error && <div style={{ color: "#ffd6d6", background: "rgba(0,0,0,0.2)", padding: 8, borderRadius: 8 }}>{error}</div>}
              {successMessage && <div style={{ color: "#dfffe1", background: "rgba(0,0,0,0.12)", padding: 8, borderRadius: 8 }}>{successMessage}</div>}

              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" disabled={submitting}
                  style={{ background: "#246e31", color: "white", padding: "10px 14px", borderRadius: 8, border: "none", fontWeight: 700, cursor: "pointer" }}>
                  {submitting ? "Creating…" : "Create partner"}
                </button>

                <Link to="/partners" style={{ alignSelf: "center", color: "white" }}>Back to partners</Link>
              </div>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
